import { ProductSelectEvent, StandardEvents } from '@shopify/events';
import { Component } from '@theme/component';
import { buildBedAddonCartItems } from '@theme/bed-addon-cart';
import { updateKlarnaPurchaseAmount } from '@theme/klarna-purchase-amount';

/** @typedef {HTMLElement & { bedAddonSelector: BedAddonSelector | null }} BedAddonProductForm */
/** @typedef {Element & { showDialog: () => void, closeDialog: () => Promise<void> }} BedAddonDialogComponent */

class BedAddonSelector extends Component {
  #abortController = new AbortController();
  /** @type {Record<string, string>} */
  #sizeMap = {};
  #currentSizeId = '';
  #currentSizeLabel = '';
  #currentParentSku = '';
  #parentPrice = 0;
  /** @type {BedAddonProductForm | null} */
  #productForm = null;
  #confirmedMattressId = '';
  #pendingMattressId = '';
  #confirmedNoMattress = false;
  #pendingNoMattress = false;
  #isConfirmingModal = false;
  #hasPendingModalChange = false;
  #klarnaUpdateFrame = 0;
  #totalAnimationTimer = 0;

  connectedCallback() {
    super.connectedCallback();
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();

    const sizeMap = this.querySelector('[data-bed-size-map]')?.textContent;
    this.#sizeMap = sizeMap ? JSON.parse(sizeMap) : {};
    this.#parentPrice = Number(this.dataset.parentPrice) || 0;
    this.#currentParentSku = this.dataset.parentSku || '';

    const formId = this.dataset.productFormId;
    const form = formId ? document.getElementById(formId) : null;
    this.#productForm = /** @type {BedAddonProductForm | null} */ (
      form?.closest('product-form-component') ?? null
    );
    if (this.#productForm) this.#productForm.bedAddonSelector = this;

    const { signal } = this.#abortController;
    this.addEventListener('change', this.#handleAddonChange, { signal });
    this.querySelector('[data-addon-dialog-component]')?.addEventListener('dialog:close', this.#handleModalClose, {
      signal,
    });
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal,
    });

    this.#syncSize(this.dataset.selectedSize || '', false);
    this.#confirmedMattressId = this.#selectedMattress?.value || '';
    this.#pendingMattressId = this.#confirmedMattressId;
    this.#syncTrigger();
    this.#syncAssemblyCards();
    this.#syncRemovalCards();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#klarnaUpdateFrame) cancelAnimationFrame(this.#klarnaUpdateFrame);
    this.#klarnaUpdateFrame = 0;
    if (this.#totalAnimationTimer) window.clearTimeout(this.#totalAnimationTimer);
    this.#totalAnimationTimer = 0;
    if (this.#productForm?.bedAddonSelector === this) this.#productForm.bedAddonSelector = null;
    this.#productForm = null;
  }

  /** @param {ProductSelectEvent} event */
  #handleProductSelect = (event) => {
    const selectedSize = event.selectedOptions?.find((option) => option.name === 'Size')?.value || '';
    this.#syncSize(selectedSize, true);

    event.promise
      ?.then(({ variant, detail }) => {
        const updatedSelector = this.id ? detail?.html?.getElementById(this.id) : null;
        if (updatedSelector instanceof HTMLElement && updatedSelector.dataset.parentSku) {
          this.#currentParentSku = updatedSelector.dataset.parentSku;
        }
        if (variant?.price?.amount) {
          this.#parentPrice = Math.round(Number(variant.price.amount) * 100);
          this.#updateTotal();
        }
      })
      .catch(() => {});
  };

  /** @param {Event} event */
  #handleAddonChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-addon-choice]')) return;

    const addonType = event.target.dataset.addonChoice || '';

    if (addonType === 'mattress' && event.target.checked) {
      for (const choice of this.querySelectorAll('[data-addon-choice="mattress"]:checked')) {
        if (choice instanceof HTMLInputElement && choice !== event.target) choice.checked = false;
      }
    }

    if (addonType !== 'removal') this.#clearError(addonType);
    if (this.#isModalOpen) {
      this.#pendingMattressId = this.#selectedMattress?.value || '';
      this.#pendingNoMattress = false;
      this.#hasPendingModalChange =
        this.#pendingMattressId !== this.#confirmedMattressId ||
        this.#pendingNoMattress !== this.#confirmedNoMattress;
      this.#syncModalSelection(true);
      return;
    }

    this.#confirmedMattressId = this.#selectedMattress?.value || '';
    this.#confirmedNoMattress = false;
    this.#syncTrigger();
    this.#syncAssemblyCards();
    this.#syncRemovalCards();
    this.#updateTotal();
  };

  #handleModalClose = () => {
    if (!this.#isConfirmingModal) this.#restoreConfirmedSelection();
    this.#isConfirmingModal = false;
    this.#hasPendingModalChange = false;
  };

  /**
   * @param {string} sizeLabel
   * @param {boolean} announceCleared
   */
  #syncSize(sizeLabel, announceCleared) {
    const sizeId = String(this.#sizeMap[sizeLabel] || '');
    if (!sizeId) return;

    this.#currentSizeId = sizeId;
    this.#currentSizeLabel = sizeLabel;
    this.dataset.selectedSize = sizeLabel;
    let cleared = false;

    for (const wrapper of this.querySelectorAll('[data-addon-choice-wrapper]')) {
      if (!(wrapper instanceof HTMLElement)) continue;
      const input = wrapper.querySelector('[data-addon-choice]');
      if (!(input instanceof HTMLInputElement)) continue;

      if (input.dataset.addonChoice === 'assembly') {
        wrapper.hidden = false;
        input.disabled = input.hasAttribute('data-unavailable');
        continue;
      }

      const compatible = input.dataset.sizeId === 'all' || input.dataset.sizeId === sizeId;
      wrapper.hidden = !compatible;
      input.disabled = !compatible || input.hasAttribute('data-unavailable');

      if (!compatible && input.checked) {
        input.checked = false;
        cleared = true;
      }
    }

    this.#clearError('mattress');
    this.#confirmedMattressId = this.#selectedMattress?.value || '';
    this.#pendingMattressId = this.#confirmedMattressId;
    if (this.#confirmedMattressId) this.#confirmedNoMattress = false;
    this.#pendingNoMattress = this.#confirmedNoMattress;
    this.#syncTrigger();
    this.#syncAssemblyCards();
    this.#syncRemovalCards();
    if (this.#isModalOpen) this.#syncModalSelection();
    this.#updateTotal();

    if (cleared && announceCleared) {
      const status = this.querySelector('[data-addon-status]');
      if (status) status.textContent = this.dataset.clearedMessage || '';
    }
  }

  #updateTotal() {
    const localTotal = this.querySelector('[data-configured-total]');
    const externalTotal = this.dataset.totalTargetId
      ? document.getElementById(this.dataset.totalTargetId)
      : null;
    const totalElement = localTotal || externalTotal;

    let total = this.#parentPrice;
    for (const selected of this.querySelectorAll('[data-addon-choice]:checked')) {
      if (selected instanceof HTMLInputElement) total += Number(selected.dataset.price) || 0;
    }

    if (totalElement) {
      const formatter = new Intl.NumberFormat(this.dataset.locale || 'en', {
        style: 'currency',
        currency: this.dataset.currency || 'GBP',
        minimumFractionDigits: total % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      });
      const formattedTotal = formatter.format(total / 100);
      totalElement.textContent = formattedTotal;
      totalElement.setAttribute('value', formattedTotal);

      if (this.#totalAnimationTimer) window.clearTimeout(this.#totalAnimationTimer);
      this.#totalAnimationTimer = 0;

      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        totalElement.setAttribute('shimmer', '');
        this.#totalAnimationTimer = window.setTimeout(() => {
          totalElement.removeAttribute('shimmer');
          this.#totalAnimationTimer = 0;
        }, 450);
      } else {
        totalElement.removeAttribute('shimmer');
      }
    }

    if (this.#klarnaUpdateFrame) cancelAnimationFrame(this.#klarnaUpdateFrame);
    this.#klarnaUpdateFrame = requestAnimationFrame(() => {
      this.#klarnaUpdateFrame = 0;
      const section = this.closest('.shopify-section');
      if (section) updateKlarnaPurchaseAmount(section, total, window);
    });
  }

  /**
   * @param {string} type
   * @param {string} message
   */
  #showError(type, message) {
    const error = this.querySelector(`[data-addon-error="${type}"]`);
    if (error instanceof HTMLElement) {
      error.textContent = message;
      error.hidden = false;

      requestAnimationFrame(() => {
        error.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
          block: 'center',
        });
      });
    }

    const trigger =
      type === 'mattress'
        ? this.querySelector('[data-addon-featured-add]') || this.querySelector('.bed-addons__trigger')
        : this.querySelector('[data-assembly-card]:not([hidden]) [data-assembly-toggle]');
    const firstChoice = this.querySelector(`[data-addon-choice="${type}"]:not(:disabled)`);
    if (trigger instanceof HTMLButtonElement) {
      trigger.setAttribute('aria-invalid', 'true');
      trigger.focus({ preventScroll: true });
    } else if (firstChoice instanceof HTMLInputElement) {
      firstChoice.focus({ preventScroll: true });
    }
  }

  /** @param {string} type */
  #clearError(type) {
    const error = this.querySelector(`[data-addon-error="${type}"]`);
    if (error instanceof HTMLElement) {
      error.textContent = '';
      error.hidden = true;
    }

    const trigger =
      type === 'mattress'
        ? this.querySelector('[data-addon-featured-add]') || this.querySelector('.bed-addons__trigger')
        : this.querySelector('[data-assembly-card]:not([hidden]) [data-assembly-toggle]');
    if (trigger instanceof HTMLButtonElement && trigger.hasAttribute('aria-invalid')) {
      trigger.removeAttribute('aria-invalid');
    }
  }

  openModal() {
    const dialogComponent = /** @type {BedAddonDialogComponent | null} */ (
      this.querySelector('[data-addon-dialog-component]')
    );
    if (!dialogComponent || typeof dialogComponent.showDialog !== 'function') return;

    this.#confirmedMattressId = this.#selectedMattress?.value || '';
    this.#pendingMattressId = this.#confirmedMattressId;
    this.#pendingNoMattress = this.#confirmedNoMattress;
    this.#hasPendingModalChange = false;
    this.#syncModalSelection();
    dialogComponent.showDialog();

    requestAnimationFrame(() => {
      const closeButton = this.querySelector('.bed-addons-modal__close');
      if (closeButton instanceof HTMLElement) closeButton.focus({ preventScroll: true });
    });
  }

  selectFeaturedMattress() {
    const selected = this.#selectedMattress;
    if (selected) {
      selected.checked = false;
      selected.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (this.classList.contains('bed-addons--bed-frame')) {
      this.openModal();
      return;
    }

    const candidate = this.querySelector(
      '[data-addon-choice-wrapper]:not([hidden]) [data-addon-choice="mattress"]:not(:disabled)'
    );
    if (!(candidate instanceof HTMLInputElement)) return;

    candidate.checked = true;
    candidate.dispatchEvent(new Event('change', { bubbles: true }));
  }

  clearModalSelection() {
    if (!this.#isModalOpen) return;
    for (const input of this.querySelectorAll('[data-addon-choice="mattress"]:checked')) {
      if (input instanceof HTMLInputElement) input.checked = false;
    }
    this.#pendingMattressId = '';
    this.#pendingNoMattress = true;
    this.#hasPendingModalChange =
      this.#pendingMattressId !== this.#confirmedMattressId ||
      this.#pendingNoMattress !== this.#confirmedNoMattress;
    this.#syncModalSelection();
  }

  async confirmModalSelection() {
    if (!this.#isModalOpen || !this.#hasPendingModalChange) return;

    this.#confirmedMattressId = this.#selectedMattress?.value || '';
    this.#pendingMattressId = this.#confirmedMattressId;
    this.#confirmedNoMattress = this.#pendingNoMattress;
    this.#isConfirmingModal = true;
    this.#syncTrigger();
    this.#updateTotal();
    this.#clearError('mattress');

    const dialogComponent = /** @type {BedAddonDialogComponent | null} */ (
      this.querySelector('[data-addon-dialog-component]')
    );
    if (dialogComponent && typeof dialogComponent.closeDialog === 'function') {
      await dialogComponent.closeDialog();
    }
  }

  async cancelModalSelection() {
    this.#restoreConfirmedSelection();
    const dialogComponent = /** @type {BedAddonDialogComponent | null} */ (
      this.querySelector('[data-addon-dialog-component]')
    );
    if (dialogComponent && typeof dialogComponent.closeDialog === 'function') {
      await dialogComponent.closeDialog();
    }
  }

  #restoreConfirmedSelection() {
    for (const input of this.querySelectorAll('[data-addon-choice="mattress"]')) {
      if (input instanceof HTMLInputElement) input.checked = input.value === this.#confirmedMattressId;
    }
    this.#pendingMattressId = this.#confirmedMattressId;
    this.#pendingNoMattress = this.#confirmedNoMattress;
    this.#syncModalSelection();
    this.#syncTrigger();
    this.#updateTotal();
  }

  #syncModalSelection(scrollToDetail = false) {
    const selected = this.#selectedMattress;
    for (const input of this.querySelectorAll('[data-addon-choice="mattress"]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const wrapper = input.closest('[data-addon-choice-wrapper]');
      if (wrapper instanceof HTMLElement) wrapper.classList.toggle('is-selected', input.checked);
    }

    const clearButton = this.querySelector('.bed-addons-modal__clear');
    clearButton?.classList.toggle('is-selected', this.#pendingNoMattress);
    clearButton?.setAttribute('aria-pressed', String(this.#pendingNoMattress));

    const prompt = this.querySelector('[data-addon-modal-prompt]');
    const promptTitle = this.querySelector('[data-addon-modal-prompt-title]');
    const promptDescription = this.querySelector('[data-addon-modal-prompt-description]');
    const detail = this.querySelector('[data-addon-modal-detail]');
    const template = selected?.closest('[data-addon-choice-wrapper]')?.querySelector('[data-addon-detail-template]');

    if (detail instanceof HTMLElement) {
      detail.replaceChildren();
      if (template instanceof HTMLTemplateElement) detail.append(template.content.cloneNode(true));
      detail.hidden = !(template instanceof HTMLTemplateElement);
    }
    if (promptTitle instanceof HTMLElement) {
      promptTitle.textContent = this.#pendingNoMattress ? 'No mattress selected' : 'Please select an option';
    }
    if (promptDescription instanceof HTMLElement) {
      promptDescription.textContent = this.#pendingNoMattress ? 'Your bed will be added without a mattress.' : '';
      promptDescription.hidden = !this.#pendingNoMattress;
    }
    if (prompt instanceof HTMLElement) prompt.hidden = template instanceof HTMLTemplateElement;

    const confirmButton = this.querySelector('[data-addon-confirm]');
    if (confirmButton instanceof HTMLButtonElement) {
      confirmButton.textContent = this.#pendingNoMattress ? 'Continue without a mattress' : 'Confirm selection';
      confirmButton.disabled =
        !this.#hasPendingModalChange || (this.dataset.mattressRequired === 'true' && !selected);
    }

    if (scrollToDetail && template instanceof HTMLTemplateElement && window.matchMedia('(max-width: 749px)').matches) {
      requestAnimationFrame(() => {
        detail?.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
          block: 'start',
        });
      });
    }
  }

  #syncTrigger() {
    const selected = this.#selectedMattress;
    const isBedFrameCard = this.classList.contains('bed-addons--bed-frame');
    const isFeaturedCard = this.classList.contains('bed-addons--bed-redesign') || isBedFrameCard;
    const compatibleChoice = this.querySelector(
      '[data-addon-choice-wrapper]:not([hidden]) [data-addon-choice="mattress"]:not(:disabled)'
    );
    const preview = selected || (!isBedFrameCard && compatibleChoice instanceof HTMLInputElement ? compatibleChoice : null);
    const value = this.querySelector('[data-addon-trigger-value]');
    const meta = this.querySelector('[data-addon-trigger-meta]');
    const price = this.querySelector('[data-addon-trigger-price]');
    const badge = this.querySelector('[data-addon-trigger-badge]');
    const addButton = this.querySelector('[data-addon-featured-add]');
    const changeButton = this.querySelector('[data-addon-choose-different]');
    const detailsButton = this.querySelector('[data-addon-view-details]');
    const selectedStatus = this.querySelector('[data-addon-selected-status]');
    const featuredCard = this.querySelector('.bed-addons__featured-mattress');
    const featuredBody = this.querySelector('.bed-addons__featured-mattress-body');
    const emptyTrigger = this.querySelector('[data-addon-empty-trigger]');
    const media = this.querySelector('[data-addon-trigger-media]');
    const image = this.querySelector('[data-addon-trigger-image]');
    const placeholder = this.querySelector('[data-addon-trigger-placeholder]');

    if (value) {
      value.textContent = isBedFrameCard
        ? selected?.dataset.addonLabel || 'Choose a Mattress'
        : isFeaturedCard
          ? preview?.dataset.addonLabel || 'No compatible mattress'
        : selected
          ? `${selected.dataset.addonLabel || ''} — ${selected.dataset.addonSize || ''}`
          : this.#confirmedNoMattress
            ? 'No mattress'
            : 'Please select an option';
    }

    if (meta instanceof HTMLElement) meta.textContent = preview?.dataset.addonSize || '';

    if (price instanceof HTMLElement) {
      const priceInCents = Number(preview?.dataset.price) || 0;
      const formatter = new Intl.NumberFormat(this.dataset.locale || 'en', {
        style: 'currency',
        currency: this.dataset.currency || 'GBP',
        minimumFractionDigits: priceInCents % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      });
      price.textContent = preview ? `+ ${formatter.format(priceInCents / 100)}` : '';
    }

    if (badge instanceof HTMLElement) {
      const defaultBadge = !isBedFrameCard && preview === compatibleChoice ? badge.dataset.defaultBadge || '' : '';
      const badgeText = preview?.dataset.addonBadge || defaultBadge;
      badge.textContent = badgeText;
      badge.hidden = !badgeText;
    }

    if (addButton instanceof HTMLButtonElement) {
      addButton.textContent = selected
        ? addButton.dataset.removeLabel || 'Remove'
        : isBedFrameCard
          ? 'Choose a Mattress'
          : addButton.dataset.addLabel || 'Add';
      addButton.disabled = isBedFrameCard ? false : !preview;
      addButton.classList.toggle('is-remove', Boolean(selected));
      addButton.setAttribute('aria-pressed', String(Boolean(selected)));
    }
    if (changeButton instanceof HTMLButtonElement) changeButton.hidden = isBedFrameCard && !selected;
    if (detailsButton instanceof HTMLButtonElement) detailsButton.hidden = !selected;
    if (selectedStatus instanceof HTMLElement) selectedStatus.hidden = !selected;
    if (featuredCard instanceof HTMLElement) featuredCard.classList.toggle('is-selected', Boolean(selected));
    if (featuredBody instanceof HTMLElement) featuredBody.hidden = isBedFrameCard && !selected;
    if (emptyTrigger instanceof HTMLButtonElement) emptyTrigger.hidden = Boolean(selected);

    const imageSrc = preview?.dataset.imageSrc || '';
    if (image instanceof HTMLImageElement && imageSrc) {
      image.src = imageSrc;
      image.alt = preview?.dataset.addonLabel || '';
    }
    if (image instanceof HTMLImageElement) {
      image.hidden = !imageSrc;
      image.style.display = imageSrc ? 'block' : 'none';
    }
    if (placeholder instanceof HTMLElement) placeholder.hidden = isBedFrameCard || Boolean(imageSrc);
    if (media instanceof HTMLElement) media.hidden = isBedFrameCard && !imageSrc;
  }

  /** @param {Event} event */
  toggleAssembly(event) {
    const target = event.target;
    const button = target instanceof Element ? target.closest('[data-assembly-toggle]') : null;
    const card = button?.closest('[data-assembly-card]');
    const input = card?.querySelector('[data-addon-choice="assembly"]');
    if (!(input instanceof HTMLInputElement) || input.disabled) return;

    const shouldSelect = !input.checked;
    for (const choice of this.querySelectorAll('[data-addon-choice="assembly"]:checked')) {
      if (choice instanceof HTMLInputElement) choice.checked = false;
    }
    input.checked = shouldSelect;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #syncAssemblyCards() {
    for (const card of this.querySelectorAll('[data-assembly-card]')) {
      const input = card.querySelector('[data-addon-choice="assembly"]');
      const button = card.querySelector('[data-assembly-toggle]');
      if (!(input instanceof HTMLInputElement) || !(button instanceof HTMLButtonElement)) continue;

      card.classList.toggle('is-selected', input.checked);
      const actionLabel = input.checked ? button.dataset.removeLabel || 'Remove' : button.dataset.addLabel || 'Add';
      if (!button.hasAttribute('data-assembly-card-toggle')) button.textContent = actionLabel;
      button.setAttribute('aria-label', `${actionLabel} ${button.dataset.assemblyName || 'Professional Assembly'}`);
      button.setAttribute('aria-pressed', String(input.checked));
    }
  }

  toggleRemoval(event) {
    const target = event.target;
    const button = target instanceof Element ? target.closest('[data-removal-toggle]') : null;
    const card = button?.closest('[data-removal-card]');
    const input = card?.querySelector('[data-addon-choice="removal"]');
    if (!(input instanceof HTMLInputElement) || input.disabled) return;

    input.checked = !input.checked;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #syncRemovalCards() {
    for (const card of this.querySelectorAll('[data-removal-card]')) {
      const input = card.querySelector('[data-addon-choice="removal"]');
      const button = card.querySelector('[data-removal-toggle]');
      if (!(input instanceof HTMLInputElement) || !(button instanceof HTMLButtonElement)) continue;

      card.classList.toggle('is-selected', input.checked);
      const actionLabel = input.checked ? button.dataset.removeLabel || 'Remove' : button.dataset.addLabel || 'Add';
      if (!button.hasAttribute('data-removal-card-toggle')) button.textContent = actionLabel;
      button.setAttribute('aria-label', `${actionLabel} ${button.dataset.removalName || 'Removal service'}`);
      button.setAttribute('aria-pressed', String(input.checked));
    }
  }

  get #selectedMattress() {
    const input = this.querySelector('[data-addon-choice="mattress"]:checked');
    return input instanceof HTMLInputElement ? input : null;
  }

  get #selectedAssembly() {
    const input = this.querySelector('[data-addon-choice="assembly"]:checked');
    return input instanceof HTMLInputElement ? input : null;
  }

  get #selectedRemovals() {
    return [...this.querySelectorAll('[data-addon-choice="removal"]:checked')].filter(
      (input) => input instanceof HTMLInputElement
    );
  }

  get #isModalOpen() {
    const dialog = this.querySelector('.bed-addons-modal');
    return dialog instanceof HTMLDialogElement && dialog.open;
  }

  /**
   * Validates required add-on choices before the product form starts its add-to-cart animation.
   * @returns {boolean}
   */
  validateSelection() {
    const mattress = this.querySelector('[data-addon-choice="mattress"]:checked');
    const assembly = this.#selectedAssembly;

    if (
      this.dataset.mattressRequired === 'true' &&
      (!(mattress instanceof HTMLInputElement) || !mattress.value)
    ) {
      this.#showError('mattress', this.dataset.mattressError || '');
      return false;
    }

    if (this.dataset.assemblyRequired === 'true' && (!assembly || !assembly.value)) {
      this.#showError('assembly', this.dataset.assemblyError || '');
      return false;
    }

    return true;
  }

  /**
   * Builds the private parent-line property consumed by order integrations.
   * The merchant-owned Addons switch controls whether the composite is submitted.
   * @param {string} parentSku
   * @param {string} mattressSku
   * @param {string} assemblySku
   * @returns {Record<string, string>}
   */
  getOrderSkuProperties(parentSku, mattressSku, assemblySku = '') {
    const normalizedParentSku = String(parentSku || '').trim();
    const normalizedMattressSku = String(mattressSku || '').trim();
    const normalizedAssemblySku = String(assemblySku || '').trim();
    if (!normalizedParentSku) return {};

    const compositeSkus = [
      this.dataset.includeMattressCompositeOrderSku === 'true' ? normalizedMattressSku : '',
      this.dataset.includeAssemblyCompositeOrderSku === 'true' ? normalizedAssemblySku : '',
    ].filter(Boolean);
    if (!compositeSkus.length) return {};

    return { _order_sku: [normalizedParentSku, ...compositeSkus].join('-') };
  }

  /** @returns {{mattressSku: string, assemblySku: string}} */
  getSelectedAddonSkus() {
    return {
      mattressSku: this.#selectedMattress?.dataset.addonSku || '',
      assemblySku: this.#selectedAssembly?.dataset.addonSku || '',
    };
  }

  /**
   * @param {string} parentVariantId
   * @param {number} quantity
   * @returns {Array<{variantId: string, quantity: number, parentId?: string, properties?: Record<string, string>}> | null}
   */
  getCartItems(parentVariantId, quantity) {
    if (!this.validateSelection()) return null;

    const mattress = this.querySelector('[data-addon-choice="mattress"]:checked');
    const assembly = this.#selectedAssembly;
    const removals = this.#selectedRemovals;
    if (mattress instanceof HTMLInputElement && mattress.value) {
      if (mattress.disabled || mattress.dataset.sizeId !== this.#currentSizeId) return null;
    }
    if (assembly?.disabled) return null;
    if (
      removals.some(
        (removal) =>
          removal.disabled || (removal.dataset.sizeId !== 'all' && removal.dataset.sizeId !== this.#currentSizeId)
      )
    )
      return null;

    return buildBedAddonCartItems({
      parentVariantId,
      parentSku: this.#currentParentSku,
      quantity,
      beddingSize: this.#currentSizeLabel,
      includeMattressCompositeOrderSku: this.dataset.includeMattressCompositeOrderSku === 'true',
      includeAssemblyCompositeOrderSku: this.dataset.includeAssemblyCompositeOrderSku === 'true',
      assemblyEligible: this.dataset.assemblyEligible === 'true',
      mattress:
        mattress instanceof HTMLInputElement && mattress.value
          ? {
              variantId: mattress.value,
              sku: mattress.dataset.addonSku || '',
              size: mattress.dataset.addonSize || this.#currentSizeLabel,
            }
          : null,
      assembly: assembly?.value ? { variantId: assembly.value, sku: assembly.dataset.addonSku || '' } : null,
      removals: removals.map((removal) => ({
        variantId: removal.value,
        sku: removal.dataset.addonSku || '',
      })),
    });
  }
}

if (!customElements.get('bed-addon-selector')) {
  customElements.define('bed-addon-selector', BedAddonSelector);
}
